import { UiState } from '../state/UiState';

export interface DesktopAmbientCallbacks {
  onUpgradeRequested: (feature: string) => void;
  getSourceCanvas: () => HTMLCanvasElement | null;
}

export class DesktopAmbientPanel {
  private tabContainer: HTMLElement;
  private rootModalContainer: HTMLElement;
  private uiState: UiState;
  private callbacks: DesktopAmbientCallbacks;

  private lockOverlayEl!: HTMLElement;
  private simulatorEl!: HTMLElement;
  private floatingWindowEl!: HTMLElement;
  private ambientCanvas!: HTMLCanvasElement;
  private actx: CanvasRenderingContext2D | null = null;
  private syncTimer: number | null = null;

  constructor(
    tabContainer: HTMLElement,
    rootModalContainer: HTMLElement,
    uiState: UiState,
    callbacks: DesktopAmbientCallbacks
  ) {
    this.tabContainer = tabContainer;
    this.rootModalContainer = rootModalContainer;
    this.uiState = uiState;
    this.callbacks = callbacks;

    this.renderTabContent();
    this.renderSimulator();
    this.bindEvents();

    this.uiState.subscribe(() => {
      this.updateView();
    });
  }

  private renderTabContent(): void {
    this.tabContainer.innerHTML = `
      <div class="pro-lock-overlay" id="lockAmbient">
        <div class="pro-lock-badge">🔒 PCアプリ版限定</div>
        <p style="font-size: 12px; color: var(--color-text-muted);">背景を透過してデスクトップの隅に推しを常駐させられます。</p>
        <button class="btn btn-primary" id="btnUpgradeAmbient" style="padding: 6px 14px; font-size: 12px;">詳細を見る</button>
      </div>

      <div style="text-align: center; padding: 20px 0;">
        <p style="margin-bottom: 12px; font-size: 13px;">作業中も邪魔にならず最前面でぷるぷる揺れます。</p>
        <button class="btn btn-primary" id="btnEnterAmbient">🪟 常駐モードを試す</button>
      </div>
    `;

    this.lockOverlayEl = this.tabContainer.querySelector('#lockAmbient') as HTMLElement;
  }

  private renderSimulator(): void {
    this.simulatorEl = document.createElement('div');
    this.simulatorEl.className = 'desktop-ambient-container';
    this.simulatorEl.id = 'ambientSimulator';

    this.simulatorEl.innerHTML = `
      <div class="ambient-floating-window" id="ambientWindow">
        <div class="ambient-toolbar">
          <span id="ambientDragHandle" style="cursor:grab;">⠿ 移動</span>
          <span id="btnPinAmbient" style="cursor:pointer;">📌 ピン</span>
          <span id="btnGhostAmbient" style="cursor:pointer;">👻 透過</span>
          <span id="btnExitAmbient" style="cursor:pointer; color:var(--color-primary); font-weight:700;">↗️ 復帰</span>
        </div>
        <div style="background: transparent; border-radius: 12px; overflow: hidden; box-shadow: 0 10px 30px rgba(0,0,0,0.3);">
          <canvas id="ambientCanvas" width="260" height="260" style="display:block;"></canvas>
        </div>
      </div>
    `;

    this.rootModalContainer.appendChild(this.simulatorEl);

    this.floatingWindowEl = this.simulatorEl.querySelector('#ambientWindow') as HTMLElement;
    this.ambientCanvas = this.simulatorEl.querySelector('#ambientCanvas') as HTMLCanvasElement;
    this.actx = this.ambientCanvas.getContext('2d');
  }

  private bindEvents(): void {
    this.tabContainer.querySelector('#btnUpgradeAmbient')?.addEventListener('click', () => {
      this.callbacks.onUpgradeRequested('デスクトップ透過常駐モード（PCアプリ版限定）');
    });

    this.tabContainer.querySelector('#btnEnterAmbient')?.addEventListener('click', () => {
      this.enterAmbientMode();
    });

    this.simulatorEl.querySelector('#btnExitAmbient')?.addEventListener('click', () => {
      this.exitAmbientMode();
    });

    this.simulatorEl.querySelector('#btnPinAmbient')?.addEventListener('click', () => {
      alert('📌 常に最前面ピン留めが切り替わりました');
    });

    this.simulatorEl.querySelector('#btnGhostAmbient')?.addEventListener('click', () => {
      alert('👻 クリック透過モードに切り替えました（キーボードショートカットで復帰可能）');
    });

    // ウィンドウドラッグ移動
    let isDragging = false;
    let startX = 0;
    let startY = 0;
    let initialLeft = 0;
    let initialTop = 0;

    const handle = this.simulatorEl.querySelector('#ambientDragHandle') as HTMLElement;
    handle.addEventListener('mousedown', (e) => {
      isDragging = true;
      startX = e.clientX;
      startY = e.clientY;
      const rect = this.floatingWindowEl.getBoundingClientRect();
      initialLeft = rect.left;
      initialTop = rect.top;
      e.preventDefault();
    });

    window.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      this.floatingWindowEl.style.left = `${initialLeft + dx}px`;
      this.floatingWindowEl.style.top = `${initialTop + dy}px`;
      this.floatingWindowEl.style.right = 'auto';
    });

    window.addEventListener('mouseup', () => {
      isDragging = false;
    });
  }

  private updateView(): void {
    const isAllowed = this.uiState.isAmbientAllowed();
    this.lockOverlayEl.style.display = isAllowed ? 'none' : 'flex';
  }

  enterAmbientMode(): void {
    this.uiState.setAmbientActive(true);
    this.simulatorEl.classList.add('active');
    this.startCanvasSync();
  }

  exitAmbientMode(): void {
    this.uiState.setAmbientActive(false);
    this.simulatorEl.classList.remove('active');
    this.stopCanvasSync();
  }

  private startCanvasSync(): void {
    const sync = () => {
      if (!this.uiState.getSnapshot().ambientActive) return;
      const srcCanvas = this.callbacks.getSourceCanvas();
      if (srcCanvas && this.actx) {
        this.actx.clearRect(0, 0, 260, 260);
        this.actx.drawImage(srcCanvas, 0, 0, 260, 260);
      }
      this.syncTimer = requestAnimationFrame(sync);
    };
    this.syncTimer = requestAnimationFrame(sync);
  }

  private stopCanvasSync(): void {
    if (this.syncTimer) {
      cancelAnimationFrame(this.syncTimer);
      this.syncTimer = null;
    }
  }
}
